import { Box } from "@kaltura/mediaspace-shared-styled";
import styled from "@emotion/styled";
import { KalturaBaseEntry } from "kaltura-typescript-client/api/types/KalturaBaseEntry";
import { MediaPlayerV3 } from "@kaltura/mediaspace-shared-player";
import { Play24Icon } from "@kaltura/ds-react-icons";
import { ReactElement, useEffect, useMemo, useState } from "react";
import { Button } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface PlayerProps {
    entry: KalturaBaseEntry;
    thumbnail: ReactElement;
    fetchModuleData: () => { ks: string };
}

const ThumbnailContainer = styled(Box)(({ theme }) => ({
    minWidth: 144,
    minHeight: 84,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    position: "relative",

    "& .playkit-pre-playback-play-overlay": {
        display: "none",
    },
}));

const ClearButton = styled(Button)(({ theme }) => ({
    padding: 0,
    border: "none",
    alignItems: "flex-start",
    height: "unset",
    width: "100%",
}));

const PlayBtn = styled(Button)(({ theme }) => ({
    width: 48,
    height: 48,
    "& svg": {
        marginLeft: 2,
        width: 36,
        height: 36,
    },
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
        width: 32,
        height: 32,
        "& svg": {
            width: 24,
            height: 24,
        },
    },
    borderRadius: "50%",
    border: "none",
    bottom: 16,
    left: 16,
    position: "absolute",
}));

const StyledDiv = styled("div")(({ theme }) => ({
    width: "100%",
    height: "100%",
    position: "relative",
}));

/**
 * explore bar player component.
 * - assuming relevant (moderation) player lib is on page
 * - when entry changes, it goes to KMS to get a KS dedicated to the entry
 *      (BE can't enable playback of all items in category using a single KS)
 * - shows entry thumbnail
 * - once it gets a KS, shows button
 * - when user clicks button, we set up the player with updated config, loadMedia and play
 */
export function Player({ entry, thumbnail, fetchModuleData }: PlayerProps) {
    const [showPlayer, setShowPlayer] = useState(false);
    const sendButtonAnalytics = useButtonAnalytics();
    const { ks } = fetchModuleData();

    const cfg = useMemo(
        () => ({
            provider: {
                partnerId: entry.partnerId,
            },
            player: {
                playback: {
                    autoplay: true,
                },
            },
        }),
        [entry.partnerId]
    );

    const media = useMemo(() => ({ entryId: entry.id, ks }), [entry.id, ks]);

    const renderPlayer = () => {
        // send analytics event
        sendButtonAnalytics("Explore sidebar - click play on player", ButtonClickAnalyticsType.LOAD, entry.id);

        // show the player
        setShowPlayer(true);
    };

    useEffect(() => {
        setShowPlayer(false);
    }, [entry]);

    if (!ks) {
        return thumbnail;
    }

    return (
        <ThumbnailContainer>
            <StyledDiv>
                {showPlayer && <MediaPlayerV3 media={media} config={cfg} />}
                {!showPlayer && (
                    <>
                        <ClearButton
                            variant="borderless"
                            onClick={renderPlayer}
                            aria-label={translate("watch %1", [entry.name])}
                        >
                            {thumbnail}
                        </ClearButton>
                        <PlayBtn
                            color={"translucent"}
                            onClick={renderPlayer}
                            aria-label={translate("watch %1", [entry.name])}
                        >
                            <Play24Icon />
                        </PlayBtn>
                    </>
                )}
            </StyledDiv>
        </ThumbnailContainer>
    );
}

export default Player;
